import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';

/**
 * Raíz del gateway, sin el sufijo `/api`.
 *
 * `environment.apiUrl` viene con `/api`, pero AuthService le saca ese sufijo y pega a
 * `${root}/auth/...` mientras el resto de la app usa `${root}/api/...`. Las dos familias
 * de requests tienen que llevar el header, así que el guard del interceptor se hace contra
 * la raíz y no contra `environment.apiUrl` (que dejaría afuera todo `/auth`).
 */
export const API_ROOT = environment.apiUrl.replace(/\/api$/, '');

/**
 * Id de dispositivo confiable del flujo 2FA "No verificar este dispositivo en los próximos
 * 30 días".
 *
 * El backend identificaba el device con una cookie httpOnly `deviceId`, pero tourbohome
 * también se sirve en dominios de marca blanca (panel.flaptek.com) mientras la API vive en
 * gateway.tourbo.com.ar: ahí la cookie es cross-site, el browser la descarta como cookie de
 * terceros y el dispositivo nunca quedaba recordado. En tourbo.com.ar sí funcionaba porque
 * comparte dominio registrable con el gateway. Ahora el id viaja por el header
 * `X-Device-Id` y se persiste acá (CAT-4314, mismo patrón que CAT-4241 en lleego).
 *
 * El valor se scopea por entorno de API: un device confiable contra un backend no significa
 * nada contra otro.
 *
 * Nota de seguridad: el id es accesible desde JS, a diferencia de la cookie que reemplaza.
 * Es un trade-off aceptado — por sí solo no otorga nada, sólo saltea el segundo factor
 * cuando se presenta junto a un JWT válido. Nunca loguear su valor.
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceIdService {
  static readonly HEADER = 'X-Device-Id';

  /**
   * Público a propósito: `AuthService.logout()` barre el storage y necesita saber qué clave
   * preservar. Es la única forma de que el dueño del dato siga siendo este servicio en vez
   * de que el string quede duplicado en el logout (CAT-4567).
   */
  readonly storageKey = `deviceId::${API_ROOT}`;

  get(): string | null {
    return localStorage.getItem(this.storageKey) || null;
  }

  set(deviceId: string): void {
    if (!deviceId) {
      return;
    }
    localStorage.setItem(this.storageKey, deviceId);
  }

  /**
   * Sólo para una revocación explícita del usuario — nunca en el logout. El id tiene que
   * sobrevivir a la sesión: borrarlo hace que el próximo login registre un device nuevo y
   * vuelva a pedir 2FA, que es justo el bug que este flujo arregla.
   *
   * `AuthService.logout()` barre el storage pero preserva `storageKey` explícitamente. No
   * agregues otro camino de logout: CAT-4567 fue exactamente eso — un helper suelto con un
   * `localStorage.clear()` pelado que se llevaba puesto el id.
   */
  clear(): void {
    localStorage.removeItem(this.storageKey);
  }
}
